import {
  saveControl,
  updateControlEvaluationField
} from '../../redux/actions/controlActions';
import { PolicyMatchingService } from './PolicyMatchingService';
import { authFetch } from '../../utils/authFetch';

export class ActionPlanningService {
  constructor(dispatch, modelId, assessmentUuid, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeActionPlanning(areaName, riskFormData, riskModel, contextData, progressCallback) {
    try {
      console.log(`[ActionPlanningService] Starting action planning for area: ${areaName}`);
      if (progressCallback) progressCallback('actions');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state - check RTK assessment slice first, fallback to legacy
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();

      // Assessment-centric approach: Read from RTK slice (riskItems), fallback to legacy (risks)
      // RTK slice uses: state.riskItems.byAssessment[assessmentUuid][areaName].areaRisks
      // Legacy uses: state.risks.byAssessment[assessmentUuid].modelRisks[areaName].areaRisks
      const rtkRisksState = currentState.riskItems?.byAssessment?.[this.assessmentUuid];
      const legacyRisksState = currentState.risks?.byAssessment?.[this.assessmentUuid];
      const assessmentState = currentState.assessments?.byId?.[this.assessmentUuid];

      // Get area risks - RTK first (with modelRisks wrapper), then legacy fallback
      const rtkAreaRisks = rtkRisksState?.modelRisks?.[areaName]?.areaRisks || {};
      const legacyAreaRisks = legacyRisksState?.modelRisks?.[areaName]?.areaRisks || {};
      const areaRisks = Object.keys(rtkAreaRisks).length > 0 ? rtkAreaRisks : legacyAreaRisks;

      // Build model data from assessment state (RTK first, then legacy fallback)
      const freshRiskModel = {
        ...legacyRisksState,
        ...rtkRisksState,
        ...assessmentState,
        templateConfig: assessmentState?.templateConfig || rtkRisksState?.templateConfig || legacyRisksState?.templateConfig,
        template_id: assessmentState?.info?.template_id || rtkRisksState?.templateId || legacyRisksState?.templateId
      };

      const evaluatedRisks = [];

      console.log(`[ActionPlanningService] 🔍 State source for area: ${areaName}`);
      console.log(`[ActionPlanningService] Assessment UUID:`, this.assessmentUuid);
      console.log(`[ActionPlanningService] Available risk sources:`, Object.keys(areaRisks));
      
      Object.entries(areaRisks).forEach(([sourceName, riskArray]) => {
        console.log(`[ActionPlanningService] Checking source "${sourceName}": ${Array.isArray(riskArray) ? riskArray.length : 'not array'} risks`);
        if (Array.isArray(riskArray)) {
          riskArray.forEach((risk, idx) => {
            console.log(`[ActionPlanningService] Risk ${idx}: "${risk.riskName}" - status: ${risk.status}, priority: ${risk.priority}`);
            if (risk.status === 'Saved' && risk.priority) {
              console.log(`[ActionPlanningService] ✅ FOUND evaluated risk for action planning: "${risk.riskName}"`);
              evaluatedRisks.push({
                ...risk,
                areaName,
                sourceName
              });
            }
          });
        }
      });

      let actionsGenerated = 0;

      // Double-check: filter out any risks not from current area
      const currentAreaRisks = evaluatedRisks.filter(risk => {
        if (risk.areaName !== areaName) {
          console.log(`[ActionPlanningService] ⚠️ SKIPPING risk from wrong area: "${risk.riskName}" (area: ${risk.areaName}, expected: ${areaName})`);
          return false;
        }
        return true;
      });
      
      console.log(`[ActionPlanningService] Processing ${currentAreaRisks.length} risks from current area (filtered from ${evaluatedRisks.length} total)`);

      // Get template configuration for RAG
      console.log('[ActionPlanningService] 🔍 DEBUG - freshRiskModel keys:', Object.keys(freshRiskModel));
      console.log('[ActionPlanningService] 🔍 DEBUG - freshRiskModel.templateConfig:', freshRiskModel.templateConfig);
      console.log('[ActionPlanningService] 🔍 DEBUG - freshRiskModel.template_id:', freshRiskModel.template_id);

      const templateConfig = freshRiskModel.templateConfig || {};
      const usePolicyRAG = templateConfig.use_control_rag === true;
      const policyTypes = templateConfig.policy_types || ['governance', 'security', 'ai-control'];
      const riskNarrative = contextData.riskNarrative || '';

      console.log('[ActionPlanningService] 📋 Template RAG config:', {
        use_control_rag: usePolicyRAG,
        policy_types: policyTypes,
        template_id: freshRiskModel.template_id,
        has_narrative: !!riskNarrative,
        templateConfig_exists: !!freshRiskModel.templateConfig
      });

      // Generate and evaluate actions per risk using existing backend
      for (const risk of currentAreaRisks) {
        try {
          // STEP 1: Always generate controls via standard AI
          console.log(`[ActionPlanningService] Generating controls via AI for risk: ${risk.riskName}`);

          const controlsResponse = await authFetch('/.netlify/functions/generateActionsByRisk', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sectionName: areaName,
              riskSourceData: { name: risk.sourceName, causes: [] },
              riskName: risk.riskName,
              riskId: risk.riskId,
              narratives: contextData,
              controlModelType: 'advanced',
              controlModelStage: 'production'
            })
          });

          if (!controlsResponse.ok) {
            console.error(`[ActionPlanningService] ❌ AI generation failed for ${risk.riskName}`);
            continue; // Skip this risk if AI generation failed
          }

          let controls = await controlsResponse.json();

          // STEP 2: If policy matching enabled, enrich controls with directive-level evidence
          if (usePolicyRAG && controls.length > 0) {
            console.log(`[ActionPlanningService] Matching ${controls.length} controls with policy directives for risk: ${risk.riskName}`);

            try {
              const policyService = new PolicyMatchingService();
              controls = await policyService.enrichControlsWithPolicies(controls, {
                policyTypes: policyTypes,
                riskNarrative: riskNarrative,
                threshold: 0.3,
                topK: 3
              });

              // Log matching results
              const matchedCount = controls.filter(c => c.policyEvidence?.length > 0).length;
              console.log(`[ActionPlanningService] ✅ ${matchedCount}/${controls.length} controls matched with policy directives`);

              // LIVE UPDATE: Stream policy matching progress
              if (this.liveUpdateCallback) {
                this.liveUpdateCallback('policyMatching', {
                  riskName: risk.riskName,
                  totalControls: controls.length,
                  matchedControls: matchedCount
                });
              }
            } catch (policyError) {
              console.error(`[ActionPlanningService] ⚠️ Policy matching error for ${risk.riskName}:`, policyError.message);
              // Continue with controls anyway (without policy evidence)
            }
          }

          if (controls.length > 0) {
            
            // Auto-select top 3 controls and save them
            const topControls = controls.slice(0, 3);
            
            // LIVE UPDATE: Stream control generation for this risk with full control details
            if (this.liveUpdateCallback) {
              this.liveUpdateCallback('controlsGenerated', {
                riskName: risk.riskName,
                controlsCount: topControls.length,
                actionsGenerated,
                controls: topControls.map(c => ({
                  controlId: c.controlId,
                  name: c.name || c.title,
                  description: c.description,
                  policyEvidence: c.policyEvidence || [],
                  source: c.source || 'AI_Generated'
                }))
              });
            }
            
            for (const control of topControls) {
              // Save control using existing Redux action with policy evidence
              this.dispatch(saveControl({
                risk,
                control: {
                  ...control,
                  policyEvidence: control.policyEvidence || [],
                  source: control.source || 'AI_Generated'
                },
                modelId: this.modelId,
                assessmentUuid: this.assessmentUuid,
                controlModelType: 'advanced',
                controlModelStage: 'production'
              }));

              // Generate evaluation for the control
              const controlEvalCfg = riskModel?.controlEvaluationForm || [];
              if (controlEvalCfg.length > 0) {
                const evalResponse = await authFetch('/.netlify/functions/generateControlEvaluationData', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    risk,
                    control,
                    evaluationConfig: controlEvalCfg
                  })
                });

                if (evalResponse.ok) {
                  const evalData = await evalResponse.json();
                  if (evalData.success && evalData.evaluationData) {
                    // Save evaluation using existing Redux action
                    this.dispatch(updateControlEvaluationField(
                      risk.riskId, 
                      control.controlId, 
                      'generated_evaluation', 
                      evalData.evaluationData
                    ));
                    
                    // LIVE UPDATE: Stream control evaluation as it completes
                    if (this.liveUpdateCallback) {
                      this.liveUpdateCallback('controlEvaluated', {
                        riskName: risk.riskName,
                        controlName: control.name || control.title,
                        evaluationData: evalData.evaluationData
                      });
                    }
                  }
                }
              }
              
              actionsGenerated++;
            }
          }
        } catch (actionError) {
          console.error(`[ActionPlanningService] Action planning failed for ${risk.riskName}:`, actionError);
        }
      }

      return {
        evaluatedRisks: evaluatedRisks.length,
        actionsGenerated,
        areaName
      };

    } catch (error) {
      console.error('[ActionPlanningService] Action planning failed:', error);
      throw error;
    }
  }
}